import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { OmdLightboxState } from '../types';

type OmdImageLightboxProps = {
  state: OmdLightboxState;
  onClose: () => void;
  onChangeIndex: (index: number) => void;
};

export function OmdImageLightbox({ state, onClose, onChangeIndex }: OmdImageLightboxProps) {
  React.useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onChangeIndex((state.index - 1 + state.pictures.length) % state.pictures.length);
      if (e.key === 'ArrowRight') onChangeIndex((state.index + 1) % state.pictures.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, onClose, onChangeIndex]);

  if (!state || state.pictures.length === 0) return null;

  const current = state.pictures[state.index];
  const hasMultiple = state.pictures.length > 1;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-slate-900/95" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <p className="text-sm font-medium text-white truncate flex-1 mr-4">{current.title}</p>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-lg border border-white/20 text-white flex items-center justify-center hover:bg-white/10"
          aria-label="Schließen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-4 relative">
        {hasMultiple && (
          <button
            type="button"
            onClick={() => onChangeIndex((state.index - 1 + state.pictures.length) % state.pictures.length)}
            className="absolute left-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            aria-label="Vorheriges Bild"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <img src={current.url} alt={current.title} className="max-w-full max-h-[70vh] object-contain" />
        {hasMultiple && (
          <button
            type="button"
            onClick={() => onChangeIndex((state.index + 1) % state.pictures.length)}
            className="absolute right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            aria-label="Nächstes Bild"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
      {hasMultiple && (
        <div className="shrink-0 px-4 pb-4 flex gap-2 overflow-x-auto justify-center">
          {state.pictures.map((pic, idx) => (
            <button
              key={`${pic.url}-${idx}`}
              type="button"
              onClick={() => onChangeIndex(idx)}
              className={`shrink-0 w-12 h-12 rounded border-2 overflow-hidden ${
                idx === state.index ? 'border-green-400' : 'border-transparent opacity-70'
              }`}
            >
              <img src={pic.urlThumbnail || pic.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
